import { createWebLocalDatabaseSyncFromServer } from './syncWebLocalDatabaseFromServer';

export const LOCAL_SYNC_STATUS = Object.freeze({
  IDLE: 'idle',
  LOCAL_READY: 'local-ready',
  SYNCING: 'syncing',
  SYNC_ERROR: 'sync-error'
});

/**
 * Creates the shared web synchronization coordinator.
 *
 * It owns the authenticated database context and the one in-flight pull for
 * every synchronized entity type. Entity read models only map records; they
 * do not start independent identity or server-sync lifecycles.
 *
 * @param {Object} [options] - Coordinator dependencies.
 * @param {Object} [options.database] - LocalDatabase adapter.
 * @param {Object} options.pullApi - Protocol v3 pull transport.
 * @param {Object} options.identity - Authenticated identity provider.
 * @returns {Object} Shared local synchronization coordinator.
 */
export const createWebLocalSyncCoordinator = ({
  database,
  pullApi,
  identity
}) => {
  const serverSync = createWebLocalDatabaseSyncFromServer({
    database,
    pullApi
  });
  let ownerIdPromise = null;
  let openedOwnerId = null;
  let openingDatabase = null;
  let syncFromServerInFlight = null;
  let resetInFlight = null;
  let lifecycleGeneration = 0;
  let syncState = { status: LOCAL_SYNC_STATUS.IDLE, error: null };
  const stateListeners = new Set();
  const entityChangeListeners = new Set();

  const notifySyncState = () => {
    const snapshot = { ...syncState };
    stateListeners.forEach((listener) => listener(snapshot));
  };

  const setSyncState = (status, error = null) => {
    syncState = { status, error };
    notifySyncState();
  };

  const notifyEntityChanges = (changesByType) => {
    const entityTypes = Object.keys(changesByType);
    if (entityTypes.length === 0) {
      return;
    }

    const snapshot = { entityTypes, changes: changesByType };
    return Promise.all(
      [...entityChangeListeners].map((listener) => listener(snapshot))
    );
  };

  const getOwnerId = () => {
    return Promise.resolve(resetInFlight).then(() => {
      if (!ownerIdPromise) {
        ownerIdPromise = Promise.resolve(identity.getStoredUserId())
          .then((userId) => {
            if (!userId) {
              throw new Error('Authenticated user identity is not available');
            }
            return userId;
          })
          .catch((error) => {
            ownerIdPromise = null;
            throw error;
          });
      }
      return ownerIdPromise;
    });
  };

  const openForOwner = (ownerId) => {
    if (openedOwnerId === ownerId) {
      return Promise.resolve();
    }

    if (openingDatabase) {
      return openingDatabase.then(() => openForOwner(ownerId));
    }

    openingDatabase = database
      .open({ ownerId })
      .then(() => {
        openedOwnerId = ownerId;
      })
      .finally(() => {
        openingDatabase = null;
      });

    return openingDatabase;
  };

  const readLocalEntities = (entityType) => {
    return getOwnerId()
      .then(openForOwner)
      .then(() => {
        return database.listEntities(entityType);
      })
      .then((entities) => {
        if (syncState.status === LOCAL_SYNC_STATUS.IDLE) {
          setSyncState(LOCAL_SYNC_STATUS.LOCAL_READY);
        }
        return entities;
      });
  };

  const readLocalEntitiesByIds = (entityType, ids) => {
    return getOwnerId()
      .then(openForOwner)
      .then(() => database.listEntitiesByIds(entityType, ids));
  };

  const syncFromServer = () => {
    if (!syncFromServerInFlight) {
      const generation = lifecycleGeneration;
      setSyncState(LOCAL_SYNC_STATUS.SYNCING);
      syncFromServerInFlight = getOwnerId()
        .then((ownerId) =>
          openForOwner(ownerId)
            .then(() =>
              serverSync.syncFromServer({
                ownerId,
                manageConnection: false
              })
            )
            .then((result) => {
              if (generation !== lifecycleGeneration) {
                return result;
              }
              openedOwnerId = ownerId;
              return Promise.resolve(
                notifyEntityChanges(result.changed_entities || {})
              ).then(() => {
                if (result.status === 'success') {
                  setSyncState(LOCAL_SYNC_STATUS.LOCAL_READY);
                } else {
                  setSyncState(LOCAL_SYNC_STATUS.SYNC_ERROR, result.error);
                }
                return result;
              });
            })
        )
        .catch((error) => {
          if (generation === lifecycleGeneration) {
            setSyncState(LOCAL_SYNC_STATUS.SYNC_ERROR, error);
          }
          throw error;
        })
        .finally(() => {
          syncFromServerInFlight = null;
        });
    }
    return syncFromServerInFlight;
  };

  const close = () => {
    if (resetInFlight) {
      return resetInFlight;
    }

    const pendingSyncFromServer = syncFromServerInFlight;
    const pendingOpen = openingDatabase;
    lifecycleGeneration += 1;
    ownerIdPromise = null;
    openedOwnerId = null;
    openingDatabase = null;
    syncFromServerInFlight = null;
    setSyncState(LOCAL_SYNC_STATUS.IDLE);
    resetInFlight = Promise.all(
      [pendingSyncFromServer, pendingOpen].filter(Boolean)
    )
      .catch(() => undefined)
      .then(() => database.close())
      .finally(() => {
        resetInFlight = null;
      });

    return resetInFlight;
  };

  return {
    readLocalEntities,
    readLocalEntitiesByIds,
    syncFromServer,
    close,
    reset: close,
    getSyncState: () => ({ ...syncState }),
    subscribeSyncState: (listener) => {
      stateListeners.add(listener);
      listener({ ...syncState });
      return () => stateListeners.delete(listener);
    },
    subscribeEntityChanges: (listener) => {
      entityChangeListeners.add(listener);
      return () => entityChangeListeners.delete(listener);
    }
  };
};
